import { MATCH, NET, defaultFlightStats, type FlightStats, type Position } from "@besenliga/shared";
import { createBalls, stepBalls, type BallsState, type PlayEvent } from "./balls.js";
import { resolveCollisions, type CollisionEvent } from "./collision.js";
import { afterCollisions, startAbilities } from "./abilities.js";
import { flyAthlete, type AthleteState, type Team } from "./flight.js";
import { NO_INPUT, type InputFrame } from "./input.js";
import {
  applyRules,
  createMatch,
  elapsedTicks,
  filterInputs,
  placeKickoff,
  type MatchState,
} from "./match.js";
import { nextInt, seedRng, type RngState } from "./rng.js";

export type { AthleteState, Heading, Team, Vec3 } from "./flight.js";

export const TICK_SECONDS = 1 / NET.simTickHz;

/** Estado completo da partida. Só dados simples: serializável, comparável e copiável. */
export interface WorldState {
  tick: number;
  rng: RngState;
  athletes: AthleteState[];
  /** Colisões do último tick (para som, efeitos e, no item 4.3, faltas). */
  events: CollisionEvent[];
  /** Bolas; ausentes no treino de voo sem bolas. */
  balls: BallsState | null;
  /** Jogadas do último tick: posse, passe, arremesso, gol, rebatida, atordoamento, captura… */
  plays: PlayEvent[];
  /** Placar, relógio e fase; ausente no treino livre (sem regras). */
  match: MatchState | null;
}

export interface AthleteSetup {
  id: string;
  team?: Team;
  role?: Position;
  stats?: FlightStats;
  /** Posição inicial; sem ela o atleta nasce num ponto sorteado perto do meio. */
  position?: { x: number; y: number; z: number };
}

export interface WorldSetup {
  seed: number;
  /** Atletas só pelo id (atributos padrão, posição sorteada). */
  athleteIds?: readonly string[];
  athletes?: readonly AthleteSetup[];
  /** Sem Pelota, Petardos e Faísca (treino de voo e testes de colisão). Padrão: com bolas. */
  balls?: boolean;
  /** Tick em que a Faísca aparece (padrão: MATCH.sparkAppearsAtSeconds). */
  sparkAtTick?: number;
  /** "partida" liga as regras (início, relógio, placar, faltas, pênalti, morte súbita). Padrão: "treino". */
  mode?: "treino" | "partida";
}

export function createWorld(setup: WorldSetup): WorldState {
  let rng = seedRng(setup.seed);
  const list: AthleteSetup[] = [
    ...(setup.athletes ?? []),
    ...(setup.athleteIds ?? []).map((id) => ({ id })),
  ].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const athletes: AthleteState[] = list.map((s, i) => {
    let x: number;
    let z: number;
    [rng, x] = nextInt(rng, -20, 20);
    [rng, z] = nextInt(rng, -12, 12);
    const position = s.position ? { ...s.position } : { x, y: 10, z };
    return {
      id: s.id,
      team: s.team ?? (i % 2 === 0 ? "casa" : "fora"),
      role: s.role ?? "artilheiro",
      position,
      velocity: { x: 0, y: 0, z: 0 },
      heading: { x: position.x <= 0 ? 1 : -1, z: 0 },
      stamina: 1,
      exhausted: false,
      turbo: false,
      stats: { ...(s.stats ?? defaultFlightStats()) },
      stunTicks: 0,
      buttons: 0,
      chargeTicks: 0,
      abilityCooldown: 0,
      abilityTicks: 0,
    };
  });
  const balls =
    setup.balls === false
      ? null
      : createBalls(setup.sparkAtTick ?? MATCH.sparkAppearsAtSeconds * NET.simTickHz);
  const match = setup.mode === "partida" && balls ? createMatch() : null;
  if (match) placeKickoff(athletes);
  return { tick: 0, rng, athletes, events: [], balls, plays: [], match };
}

/**
 * Avança um tick. Função pura: o mesmo estado e os mesmos comandos sempre dão o mesmo resultado.
 * Proibido aqui: Math.random, relógio, ordem de inserção de objetos, `**` e funções trigonométricas,
 * que podem variar entre navegadores. Use só + - * / e Math.sqrt.
 */
export function step(state: WorldState, rawInputs: InputFrame): WorldState {
  const match = state.match ? cloneMatch(state.match) : null;
  const inputs = match ? filterInputs(match, rawInputs, state.athletes) : rawInputs;
  const before = state.athletes.map(cloneAthlete);
  // Habilidades só valem com as bolas em jogo (partida e treino com bolas).
  const started = state.balls ? startAbilities(before, inputs) : { activated: new Set<string>(), plays: [] };
  const athletes = before.map((a) => flyAthlete(a, inputs[a.id] ?? NO_INPUT, TICK_SECONDS));
  const events = resolveCollisions(athletes);
  const tick = state.tick + 1;
  if (!state.balls) return { tick, rng: state.rng, athletes, events, balls: null, plays: [], match };
  const crashes = afterCollisions(athletes, events);
  if (match?.phase === "fim")
    return { tick, rng: state.rng, athletes, events, balls: state.balls, plays: [], match };
  // Na partida, a Faísca segue o relógio de jogo (que para nas pausas), não o tick.
  const ballTick = match ? elapsedTicks(match) : tick;
  const r = stepBalls(state.balls, athletes, inputs, events, ballTick, state.rng, started.activated);
  const plays = [...started.plays, ...crashes, ...r.plays];
  if (match) applyRules(match, athletes, r.balls, events, plays);
  return { tick, rng: r.rng, athletes, events, balls: r.balls, plays, match };
}

function cloneMatch(m: MatchState): MatchState {
  return {
    ...m,
    score: { ...m.score },
    goals: { ...m.goals },
    fouls: { ...m.fouls },
    penalty: m.penalty ? { ...m.penalty } : null,
    blockTicks: { ...m.blockTicks },
    stats: Object.fromEntries(Object.entries(m.stats).map(([k, v]) => [k, { ...v }])),
    pendingPass: m.pendingPass ? { ...m.pendingPass } : null,
  };
}

function cloneAthlete(a: AthleteState): AthleteState {
  return {
    ...a,
    position: { ...a.position },
    velocity: { ...a.velocity },
    heading: { ...a.heading },
    stats: { ...a.stats },
  };
}
